"use client";

// In-memory, session-scoped cache for a patient's saved shipping addresses.
//
// Addresses are personal information, so this cache follows the same
// safeguards as the prescriptions cache (PIPEDA: limiting retention /
// safeguards):
//   - it lives ONLY in browser memory — never localStorage/sessionStorage,
//   - it is keyed by token + patientId so a different account on the same
//     browser can never read another user's addresses,
//   - it is wiped on logout via clearAddressesCache().
//
// Unlike server-mutated data, addresses only ever change through the user's
// own add/edit/delete actions in this app, so we cache until a mutation
// invalidates the entry (invalidateAddresses) rather than aggressively
// revalidating. Every save path MUST call invalidateAddresses.
//
// The "use client" directive keeps this module out of the server bundle, so
// the Map is never shared across requests/users on the server.

import { fetchShippingAddresses, type SavedShippingAddress } from "@/lib/api/customer";

const CACHE_TTL_MS = 2 * 60 * 1000;

type CacheEntry = { fetchedAt: number; data: SavedShippingAddress[] };
type AddressesResponse = Awaited<ReturnType<typeof fetchShippingAddresses>>;

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<AddressesResponse>>();

function keyOf(token: string, patientId: string) {
  return `${token}|${patientId}`;
}

function isFresh(entry: CacheEntry | undefined): entry is CacheEntry {
  return !!entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS;
}

/** Any cached data, even if stale — for instant paint before revalidating. */
export function peekAddresses(
  token?: string | null,
  patientId?: string | null,
): SavedShippingAddress[] | null {
  if (!token || !patientId) return null;
  return cache.get(keyOf(token, patientId))?.data ?? null;
}

/**
 * Return fresh cached addresses, or fetch + cache. Concurrent callers share
 * one in-flight request. The result shape matches fetchShippingAddresses so
 * callers can keep their existing `res.ok` / `res.data.addresses` handling.
 * Errors are surfaced and never cached.
 */
export function getAddresses(token: string, patientId: string): Promise<AddressesResponse> {
  const key = keyOf(token, patientId);
  const entry = cache.get(key);
  if (isFresh(entry)) {
    return Promise.resolve({ ok: true, data: { addresses: entry.data } } as AddressesResponse);
  }
  const existing = inflight.get(key);
  if (existing) return existing;

  const req = fetchShippingAddresses(token, patientId)
    .then((res) => {
      if (res.ok) cache.set(key, { data: res.data.addresses, fetchedAt: Date.now() });
      return res;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, req);
  return req;
}

/**
 * Drop cached addresses so the next getAddresses() refetches. Call after any
 * add / edit / delete so account and checkout never show a stale list.
 */
export function invalidateAddresses(token?: string | null, patientId?: string | null) {
  if (!token || !patientId) {
    cache.clear();
    inflight.clear();
    return;
  }
  const key = keyOf(token, patientId);
  cache.delete(key);
  inflight.delete(key);
}

/** Wipe all cached addresses. Call on logout / account switch. */
export function clearAddressesCache() {
  cache.clear();
  inflight.clear();
}
